import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const FoodAsCare = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Community</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              Food as Care – The Power of Shared Meals
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>July 26, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>4 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/9a960794-7883-47e3-83c4-865469e41cc7.png" 
              alt="Person preparing fresh food with lime being squeezed over colorful bowl representing care through cooking"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              Food is more than fuel. It is a language of care, connection, and community. Shared meals have been part of human culture for millennia, serving as a foundation for relationships and rituals.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The science of shared meals</h2>

              <p>
                Research shows that families who eat together regularly experience <strong className="text-accent">stronger bonds, better communication, and lower rates of depression among adolescents</strong> (Fiese et al., 2006). Shared meals are also linked to healthier eating habits and greater life satisfaction.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Beyond nutrition</h2>

              <p>
                Cooking and eating together is a form of mindfulness. It slows us down, engages the senses, and creates opportunities for meaningful conversations. In workplaces and schools, <strong className="text-accent">communal meals foster inclusion and collaboration</strong>.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Practical ways to bring it back</h2>

              <ul className="space-y-3 ml-6">
                <li><strong className="text-accent">Make at least one meal a day</strong> a phone-free, shared experience.</li>
                <li><strong className="text-accent">Rotate cooking responsibilities</strong> in families or among friends.</li>
                <li><strong className="text-accent">Use meals as an opportunity</strong> to check in emotionally, not just physically.</li>
              </ul>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Conclusion</h2>

              <p>
                Meals are not just about nourishment – they are about connection. By reclaiming the power of shared meals, we nurture both body and soul.
              </p>

              <p className="text-xl font-light italic text-accent">
                In every shared meal, love finds its way to the table.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• Fiese, B. H., Foley, K. P., & Spagnola, M. (2006). Routine and ritual elements in family mealtimes: Contexts for child well-being and family identity. <em>New Directions for Child and Adolescent Development, 2006</em>(111), 67–89.</p>
                  <p>• Dunbar, R. I. M. (2017). Breaking bread: The functions of social eating. <em>Adaptive Human Behavior and Physiology, 3</em>(3), 198–211.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default FoodAsCare;